
export const clientId = '735b1eb810304bba966af0891ab54053';
export const conversionClientId = 'ee73cf45f6254d16a7d44842d4fee066';

/**
 * Auto-detect app stage from URL. Uses the current location by default.
 * @param {string} [url] URL to use for detection. Defaults to window URL
 * @return {StageName} app stage (local, dev, test, prod)
 */
export function detectStage(url = window.location.href) {
    const parsed = new URL(url);
    const host = parsed.hostname;
    switch (host) {
        case 'apps.dev.nuskin.io':
        case 'dev.nuskin.com':
            return 'dev';
        case 'localhost':
            return 'local';
        case 'test.nuskin.com':
            return 'test';
        case 'www.nuskin.com':
            return 'prod';
    }

    // AEM feature branch
    if (host.endsWith('dev.usw2.nuskin.io') || host.endsWith('aem.dev.nuskin.io')) {
        return 'test';
    }

    if (host.endsWith('mynuskin.com')) {
        if (host.endsWith('test.mynuskin.com')) {
            return 'test';
        } else {
            return 'prod';
        }
    }

    if (parsed.port) {
        return 'local';
    }

    return 'prod';
}

/**
 * @typedef {('local'|'dev'|'test'|'prod')} StageName
 */

/**
 * @typedef {{}} StageConfig
 * @prop {StageName} stage stage name
 * @prop {string} service service environment name
 * @prop {string} aem aem environment name
 * @prop {string} [featureFlagUser] user to pass to feature flag server
 */

/** @constant {StageConfig} */
const LOCAL = {
    stage: 'local',
    service: 'test',
    aem: 'test',
    oktaConfig: {
        issuer: 'https://testid.nuskin.com/oauth2/default',
        clientId: '0oa9s44f3nsVki5Vf1d6'
    },
    awsBaseUrl: 'https://testapi.cloud.nuskin.com'
};

/** @constant {StageConfig} */
const DEV = {
    stage: 'dev',
    service: 'test',
    aem: 'test',
    oktaConfig: {
        issuer: 'https://testid.nuskin.com/oauth2/default',
        clientId: '0oa9s44f3nsVki5Vf1d6'
    },
    awsBaseUrl: 'https://devapi.cloud.nuskin.com'
};

/** @constant {StageConfig} */
const TEST = {
    stage: 'test',
    service: 'test',
    aem: 'test',
    oktaConfig: {
        issuer: 'https://testid.nuskin.com/oauth2/default',
        clientId: '0oa9s44f3nsVki5Vf1d6'
    },
    awsBaseUrl: 'https://testapi.cloud.nuskin.com'
};

/** @constant {StageConfig} */
const PROD = {
    stage: 'prod',
    service: 'prod',
    aem: 'prod',
    oktaConfig: {
        issuer: 'https://id.nuskin.com/oauth2/default',
        clientId: '0oa39ghqbi12EDPiH5d6'
    },
    awsBaseUrl: 'https://api.cloud.nuskin.com'
};

/**
 * Get the config for a given stage
 * @param {StageName} stage name
 * @return {StageConfig} config
 */
export function configForStage(stage) {
    switch (stage) {
        case 'local':
            return getLocalConfigWithOverrides();
        case 'dev':
            return DEV;
        case 'test':
            return TEST;
        case 'prod':
            return PROD;
        default:
            return PROD;
    }
}

/** @constant {StageConfig} */
const detected = configForStage(detectStage());

/** @constant {StageConfig} */
export default detected;

/**
 * Gets local config, taking overrides into account.
 * @return {StageConfig} stage config
 */
function getLocalConfigWithOverrides() {
    return Object.assign({}, LOCAL, getEnvVarOverrides(), getSessionStorageOverrides());
}

/**
  * Gets any local env overrides stored in session storage
  * @return {{}} overrides
  */
function getSessionStorageOverrides() {
    /* istanbul ignore else - it's really quite impossible to get window to be falsy */
    if (window && window.sessionStorage) {
        const value = window.sessionStorage.getItem('$appStageOverrides');
        if (value) {
            return JSON.parse(value);
        }
    }
    return {};
}

/**
  * Gets any local env overrides stored in env vars SIGNUP_SERVICE_ENV and SIGNUP_AEM_ENV
  * @return {{}} overrides
  */
function getEnvVarOverrides() {
    /* istanbul ignore next - it's really quite impossible to get process to be falsy */
    if (!process || !process.env) {
        return {};
    }
    const result = {};
    if (process.env.SIGNUP_SERVICE_ENV) {
        result.service = process.env.SIGNUP_SERVICE_ENV;
    }
    if (process.env.SIGNUP_AEM_ENV) {
        result.aem = process.env.SIGNUP_AEM_ENV;
    }
    if (Object.keys(result).length > 0) {
        console.debug('Got app stage overrides from environment:', result);
    }
    return result;
}

